import React from 'react'

/**
 * Whether this user may upload their own picture.
 *
 * # It used to call a global that does not exist
 *
 * ```js
 * // import Comet from "@codecraftkit/comet";
 * …
 * const res = await CometInstance.call('agency.get', { _id: item?.agency?._id })
 * ```
 *
 * The import is commented out, so `CometInstance` was an undefined global and the line threw a
 * ReferenceError — swallowed by the `try/catch` around it, which only logged when
 * `NEXT_PUBLIC_METEOR_DEBUG` was on. `setUserAllowToUploadImage` therefore never ran and the flag
 * stayed `false`: passing `getUserPermissionToUploadImage` HID the upload tab instead of gating
 * it. Two `console.warn`s left in the code said as much — "no usar la instancia de CometInstance
 * de forma global, por favor migrar".
 *
 * # The prop now does what its name says
 *
 * `getUserPermissionToUploadImage` is the CHECK, not a flag asking for one: it receives the item
 * and returns (or resolves to) whether uploading is allowed. That removes the global and puts the
 * backend call where the app already has its client.
 *
 * Not passing it at all keeps the old meaning — everyone may upload.
 */
export const useGetUserPermissionToUploadImage = (item, getUserPermissionToUploadImage) => {
  const [userAllowToUploadImage, setUserAllowToUploadImage] = React.useState(false)

  React.useEffect(() => {
    if (!getUserPermissionToUploadImage) {
      setUserAllowToUploadImage(true)
      return
    }

    if (typeof getUserPermissionToUploadImage !== 'function') {
      warnNotAFunction()
      setUserAllowToUploadImage(false)
      return
    }

    let cancelled = false

    Promise.resolve(getUserPermissionToUploadImage(item))
      .then(allowed => { if (!cancelled) setUserAllowToUploadImage(Boolean(allowed)) })
      // The consumer owns the call, so it owns the error. What this must NOT do is leave the flag
      // in whatever state a rejection found it in.
      .catch(() => { if (!cancelled) setUserAllowToUploadImage(false) })

    return () => { cancelled = true }
  }, [item, getUserPermissionToUploadImage])

  return [userAllowToUploadImage]
}

let warned = false
const warnNotAFunction = () => {
  if (warned || process.env.NODE_ENV === 'production') return
  warned = true
  console.warn(
    '[@codecraftkit] <ProfileImage getUserPermissionToUploadImage> has to be a FUNCTION now.\n' +
    '  It used to be a flag that made the component call a Meteor global which was never ' +
    'imported — so it threw, the error was swallowed, and the upload tab stayed hidden.\n' +
    '  Pass the check itself: `getUserPermissionToUploadImage={item => api.canUpload(item)}`.\n' +
    '  Guide: docs/migracion-chakra-a-tailwind.md'
  )
}
